import { CODIGOS_ERRO } from '@remoto/shared';
import {
  EntregaEmAndamentoSchema,
  prazoDaEntregaMs,
  SessaoEntregaSchema,
  type AnexoDaMensagem,
  type EntregaEmAndamento,
  type SessaoEntrega,
} from '@remoto/protocolo';
import { mensagemDoCodigo } from '../erros';

/**
 * A tira de espera da conversa: o que você mandou e ainda não apareceu nela.
 *
 * Tudo aqui é puro. O estado mora em `envios.ts` (fora de qualquer
 * componente, por sessão), mas as regras — o que uma resposta com `entrega`
 * faz com o item, o que o fim da entrega faz, quando o desfecho vira
 * "desconhecido" — ficam à parte para serem provadas sem montar a tela. Elas
 * importam porque a entrega em segundo plano tem finais que a pessoa precisa
 * distinguir, e um erro que chega pelo socket horas depois do `POST` não tem
 * exceção nenhuma para o `onError` pegar.
 */

/** Como uma entrega em segundo plano terminou, quando não terminou bem. */
export type DesfechoDaEntrega =
  /** A máquina disse que não entregou. O `recado` é a frase do catálogo. */
  | { tipo: 'falhou'; entregaId: string; recado: string }
  /**
   * Ninguém disse nada: o prazo passou, ou o socket caiu e voltou (o evento
   * pode ter ficado pelo caminho, e a entrega vive na memória da API). O
   * desfecho é DESCONHECIDO — não é erro, e por isso não há "tentar de novo".
   */
  | { tipo: 'sem-confirmacao'; entregaId: string };

/** Uma mensagem esperando a vez, do lado de cá. */
export interface NaFila {
  id: string;
  /** A conversa a que ela pertence: a resposta do `POST` pode chegar com outra aberta. */
  sessaoId: string;
  texto: string;
  anexos: AnexoDaMensagem[];
  erro?: string;
  /** Saiu com a conversa dormindo: a API acorda antes de entregar, e isso leva segundos. */
  acordando?: boolean;
  /**
   * A API ACEITOU a mensagem e a máquina ainda está baixando os anexos
   * (`ENTREGA_EM_SEGUNDO_PLANO_BYTES`). Some quando chega o `sessao.entrega`
   * com este `entregaId`, ou quando vira `desfecho`. Enquanto existir, o Enviar
   * da conversa fica fechado — ver `emEntrega`.
   */
  entrega?: EntregaEmAndamento;
  desfecho?: DesfechoDaEntrega;
}

/**
 * As frases do envio que o catálogo geral diz de outro jeito.
 *
 * Dizia "A máquina não recebeu a mensagem" para QUALQUER recusa — e em
 * 2026-09-25 isso escondeu uma máquina ótima respondendo "não tenho essa
 * conversa". O `nao_encontrado` do catálogo fala de caminho de arquivo; aqui
 * ele é a conversa.
 */
export const FRASES_DO_ENVIO: Record<string, string> = {
  anexo_invalido: 'O anexo não chegou ao servidor. Tente anexar de novo.',
  nao_encontrado:
    'A máquina não encontrou esta conversa. Use Retomar, ou comece outra na mesma pasta.',
};

/** Quando a recusa não traz um código que o catálogo conheça. */
export const FRASE_PADRAO_DO_ENVIO = 'A máquina não recebeu a mensagem.';

/**
 * A frase do desfecho desconhecido. Manda CONFERIR antes de mandar de novo: a
 * mensagem pode ter chegado, e repeti-la faria a sessão responder duas vezes.
 */
export const FRASE_SEM_CONFIRMACAO =
  'Sem confirmação da entrega — confira a conversa antes de mandar de novo.';

/**
 * Quanto esperar o fim da entrega antes de desistir de esperar.
 *
 * O dobro do prazo que a API dá à máquina (`prazoDaEntregaMs`) mais um minuto: o
 * evento só chega depois de a máquina responder à API, e dar menos que isso
 * declararia "sem confirmação" sobre uma entrega que ainda está dentro do
 * prazo dela. O dobro cobre a máquina lenta que ainda assim acaba, e é um
 * teto finito — sem ele a frase "recebendo os anexos" mentiria para sempre
 * se o evento se perdesse.
 */
export function prazoSemConfirmacaoMs(bytes: number): number {
  return 2 * prazoDaEntregaMs(bytes) + 60_000;
}

/**
 * A `entrega` que a resposta do `POST /enviar` traz, se trouxer.
 *
 * Valida com o schema do contrato e devolve `null` para qualquer outra forma:
 * resposta sem `entrega` é o caminho síncrono de sempre, e uma `entrega`
 * malformada não pode virar um item preso em "entregando" para sempre, sem
 * nada que o libere.
 */
export function lerEntrega(resposta: unknown): EntregaEmAndamento | null {
  if (typeof resposta !== 'object' || resposta === null) return null;
  const lida = EntregaEmAndamentoSchema.safeParse((resposta as { entrega?: unknown }).entrega);
  return lida.success ? lida.data : null;
}

/** O `sessao.entrega` do socket, ou `null` se a carga não for do contrato. */
export function lerFimDaEntrega(carga: unknown): SessaoEntrega | null {
  const lida = SessaoEntregaSchema.safeParse(carga);
  return lida.success ? lida.data : null;
}

/** O id da entrega a que o item se refere, esteja ela em curso ou já com desfecho. */
export function entregaDoItem(item: NaFila): string | undefined {
  return item.entrega?.entregaId ?? item.desfecho?.entregaId;
}

/**
 * A resposta do envio chegou: com `entrega`, o item passa a "entregando"; sem,
 * só deixa de "acordar" — o caminho síncrono de sempre.
 *
 * O `acordando` cai junto porque o `POST` só responde depois de a API acordar a
 * conversa — o que ainda dura é o download dos anexos, e dizer "acordando"
 * por cima disso seria mentir sobre o que está acontecendo. Uma tentativa nova
 * (`tentar de novo`) substitui a entrega e o desfecho da anterior.
 */
export function iniciarEntrega(
  fila: readonly NaFila[],
  itemId: string,
  entrega: EntregaEmAndamento | null,
): NaFila[] {
  return fila.map((item) => {
    if (item.id !== itemId) return item;
    const { entrega: _anterior, desfecho: _desfecho, ...resto } = item;
    return { ...resto, acordando: false, ...(entrega ? { entrega } : {}) };
  });
}

/**
 * A falha de uma entrega, em uma frase; `null` quando deu certo.
 *
 * A MESMA tradução do `onError` do envio: o código da carga é o que a rota
 * teria devolvido se tivesse esperado, e a frase vem do catálogo, nunca do texto
 * cru do servidor. Sem código, a frase padrão do envio.
 */
export function recadoDoFim(fim: SessaoEntrega): string | null {
  return fim.ok ? null : mensagemDoCodigo(fim.codigo, FRASE_PADRAO_DO_ENVIO, FRASES_DO_ENVIO);
}

/**
 * O fim da entrega chegou pelo socket.
 *
 * `ok: true` tira o item do estado de entrega (e apaga um desfecho
 * "desconhecido" que ficou pelo caminho): ele continua na tira até o
 * transcript mostrar a mensagem, como qualquer outro. `ok: false` vira
 * `desfecho: falhou`, e o botão "tentar de novo" volta a existir.
 *
 * **Exceto `entrega_sem_confirmacao`**, que a API manda quando o prazo do ack
 * venceu ou a máquina caiu no meio do download: o daemon pode ter entregado
 * mesmo assim (ele segue baixando e responde a um socket que já não existe).
 * É DESCONHECIDO, e vira o mesmo estado neutro do prazo e da reconexão — sem
 * "tentar de novo", que repetiria uma mensagem talvez já entregue, e com o
 * Enviar aberto. Tratar como falha comum seria dizer "não chegou" sobre algo
 * que ninguém sabe.
 *
 * Casa pelo `entregaId`, também com o item em "sem confirmação": o evento que
 * se achava perdido pode chegar depois, e um desfecho verdadeiro vale mais que
 * um desconhecido. Quem escolhe a LISTA certa (a da conversa do evento) é o
 * store; o evento vai à sala do USUÁRIO e chega a todas as abas dele.
 */
export function aplicarFimDaEntrega(fila: readonly NaFila[], fim: SessaoEntrega): NaFila[] {
  const recado = recadoDoFim(fim);
  const desconhecido = !fim.ok && fim.codigo === CODIGOS_ERRO.entregaSemConfirmacao;
  return fila.map((item) => {
    if (entregaDoItem(item) !== fim.entregaId) return item;
    const { entrega: _terminada, desfecho: _anterior, ...resto } = item;
    if (desconhecido) {
      return { ...resto, desfecho: { tipo: 'sem-confirmacao', entregaId: fim.entregaId } };
    }
    return recado === null
      ? resto
      : { ...resto, desfecho: { tipo: 'falhou', entregaId: fim.entregaId, recado } };
  });
}

/**
 * Passa a "sem confirmação" o item que esperava esta entrega — ou, sem id,
 * todos os que esperam. Só toca quem está EM entrega: um desfecho verdadeiro
 * que já chegou não se rebaixa a desconhecido.
 */
export function marcarSemConfirmacao(fila: readonly NaFila[], entregaId?: string): NaFila[] {
  return fila.map((item) => {
    if (!item.entrega) return item;
    if (entregaId !== undefined && item.entrega.entregaId !== entregaId) return item;
    const { entrega, ...resto } = item;
    return { ...resto, desfecho: { tipo: 'sem-confirmacao', entregaId: entrega.entregaId } };
  });
}

/**
 * Por que a mensagem não chegou, quando não chegou: a recusa do `POST` (`erro`)
 * ou o `falhou` da entrega. `null` para quem espera, entrega ou está sem
 * confirmação — este último é dúvida, não falha.
 */
export function motivoDaFalha(item: NaFila): string | null {
  if (item.erro !== undefined) return item.erro;
  return item.desfecho?.tipo === 'falhou' ? item.desfecho.recado : null;
}

/**
 * Há mensagem desta conversa com anexo ainda a caminho da máquina?
 *
 * É o que fecha o Enviar. O daemon trata cada `sdk.enviar` em paralelo —
 * baixa os anexos e SÓ ENTÃO entrega ao modelo —, então uma segunda mensagem
 * sem anexo chegaria ao modelo ANTES da primeira ("foque no minuto 3" antes de
 * "analise este vídeo"). Com a entrega em segundo plano o `POST` volta em
 * milissegundos e nada mais segurava a pessoa: a ordem é do painel.
 *
 * Só `entrega` conta. Falha e "sem confirmação" liberam: no primeiro caso a
 * mensagem não vai chegar, e no segundo esperar mais seria prender a pessoa
 * por um desfecho que ninguém vai dizer.
 */
export function emEntrega(fila: readonly NaFila[]): boolean {
  return fila.some((item) => item.entrega !== undefined);
}

/**
 * Tira da fila o que já apareceu na conversa.
 *
 * Compara pelo começo, não por igualdade: o daemon acrescenta a lista de
 * anexos ao texto antes de entregar, então a mensagem que volta é a sua com
 * um rabo. Não toca em quem está em entrega ou falhou: antes de a entrega
 * acabar a mensagem não pode estar no transcript, e um texto IGUAL de ontem
 * apagaria a linha — e com ela a frase que explica por que o Enviar está
 * fechado, ou a falha que a pessoa ainda não viu.
 */
export function removerJaVistas(fila: readonly NaFila[], ditas: readonly string[]): NaFila[] {
  return fila.filter((item) => {
    if (item.entrega || item.desfecho?.tipo === 'falhou') return true;
    return !ditas.some((dita) => dita.startsWith(item.texto.trim()));
  });
}

/**
 * Fins de entrega que chegaram ANTES de a resposta do `POST` ser processada.
 *
 * O socket e o `fetch` são caminhos distintos, e uma entrega que a máquina
 * conclui em poucos milissegundos pode ter o fim entregue antes de a
 * promessa do `POST` resolver — aí não há item "entregando" para casar, e o
 * evento seria perdido: a tira ficaria em "recebendo os anexos" para sempre.
 * Guarda os últimos, com teto, porque o mesmo evento chega a todas as abas da
 * pessoa e quase todos são de entregas que esta tela nunca pediu.
 */
export const TETO_DE_FINS_GUARDADOS = 20;

export function guardarFimAntecipado(
  guardados: ReadonlyMap<string, SessaoEntrega>,
  fim: SessaoEntrega,
): Map<string, SessaoEntrega> {
  const novo = new Map(guardados);
  novo.delete(fim.entregaId);
  novo.set(fim.entregaId, fim);
  while (novo.size > TETO_DE_FINS_GUARDADOS) {
    const maisAntigo = novo.keys().next().value;
    if (maisAntigo === undefined) break;
    novo.delete(maisAntigo);
  }
  return novo;
}
